import { Item, Property } from "../types";



/**
 * indica se ci sono CHILDREN da visualizzare o no
 */
export function showChild(item: Item): boolean {
	return !item.collapsed && !!item.children && item.children.length > 0;
}

/**
 * Costruisce un albero a partire dai dati e dalle proprietà
 */
export function buildTreeItems(data: any[], properties: Property[], indexes?: number[], depth: number = 0): Item[] {
	if (!properties.length) return [];
	const [currentProp, ...restProps] = properties;

	// costruisco i gruppi in base alla proprietà corrente
	const grouped = data.reduce<{ [key: string]: { items: any[]; indexes: number[]; }; }>(
		(acc, curr, idx) => {
			const val = curr[currentProp.name];
			if (!acc[val]) acc[val] = { items: [], indexes: [] };
			acc[val].items.push(curr);
			acc[val].indexes.push(indexes ? indexes[idx] : idx);
			return acc;
		}, {});

	// costruisco l'albero
	const items = Object.entries(grouped).map(([groupValue, group]) => {
		return {
			value: group.items[0][currentProp.name],
			collapsed: currentProp.startCollapsed ?? false,
			children: restProps.length ? buildTreeItems(group.items, restProps, group.indexes, depth + 1) : null,
			indexes: group.indexes,
			totals: [],
			depth,
			count: 0,
		} as Item;
	});

	return !!currentProp.sort ? items.sort(currentProp.sort) : items;
}

/**
 * esplora gli items in profondità
 */
export function exploreItems(items: Item[], fn: (item: Item, depth: number) => void, depth = 0) {
	if (!items) return;
	for (let index = 0; index < items.length; index++) {
		const item = items[index];
		fn(item, depth);
		if (!!item.children) {
			exploreItems(item.children, fn, depth + 1);
		}
	}
}

/**
 * Aggiorna tutti i "totals" degli item in profondità
 */
export function updateTotals(items: Item[], data: any[], valueType: Property[]) {
	exploreItems(items, item => item.totals = calcValue(item.indexes, data, valueType));
}

/**
 * Restituisce i "totals" di un gruppo di "indexes" su "data" per ogni VALUE PROPERTY
 */
export function calcValue(indexes: number[], data: any[], properties: Property[]) {
	const value = properties.map(property => calcValueIndexes(indexes, data, property))
	return value
}

export function calcValueIndexes(indexes: number[], data: any[], property: Property) {
	if ( indexes.length == 0 ) null
	if ( indexes.length == 1 ) return data[indexes[0]][property.name]
	return indexes?.reduce((acc, dataIndex, i) => {
		const value = data[dataIndex][property.name]

		if (!!property.fnGroup) {
			if (i == 0) return value // se è il primo valore non posso usare la funzione di gruppo
			return property.fnGroup(acc, value)
		}
		// se è un numero utilizzo la funzione di gruppo di default (la somma)
		if ( acc == null ) return value
		if (typeof value === 'number' && !isNaN(value)) return acc + value
		if (value == null && acc != null) return acc
		return value

	}, null)
}

export function calcValues(values: any[], valueType: Property) {
	return values.reduce((acc, value, i) => {
		if (i == 0) return value
		if (!!valueType.fnGroup) return valueType.fnGroup(acc, value)
		if (typeof value === 'number' && !isNaN(value)) return acc + value
		return value
	}, null)
}


/**
 * Restituisce gli indici condivisi tra due item
 */
export function getSharedIndexes(rowItem: Item, colItem: Item): number[] {
	if (!rowItem && !colItem) return [];
	return !colItem ? rowItem.indexes : !rowItem ? colItem.indexes
		: rowItem.indexes?.filter(idx => colItem.indexes?.includes(idx)) ?? [];
}

/**
 * linearizza la struttura ad albero della COLS e ROWS alle sole foglie. inserisce l'indice "count" per ogni foglia
 */
export function normalizeItems(items: Item[], count = { i: 0 }): Item[] {
	if (!items) return [];
	const ret: Item[] = [];

	for (let index = 0; index < items.length; index++) {
		const item = items[index];
		const visibleChildren = showChild(item);
		if (visibleChildren) {
			const nodes = normalizeItems(item.children, count);
			ret.push(...nodes);
		} else {
			item.count = count.i++;
			ret.push(item);
		}
	}

	return ret;
}

/** restituisce un Item tramite l'index dei dati */
export function getItemByIndex(items: Item[], index: number): Item {
	if (!items) return null;
	for (const item of items) {
		if (item.indexes?.includes(index)) return item;
	}
	return null;
}
